import apiRequest from 'utils/apiRequest'

import { UPDATE_DOC } from 'EditorApp/actionTypes'
import { makeBackoffDelay, retryWithDelay } from 'utils/retry'
import Logger from 'utils/logger'
import type { DocState } from 'EditorApp/types'

export function updateDoc(attrs: Partial<DocState>) {
  return { type: UPDATE_DOC.action, attrs }
}

class ExportPendingError extends Error {
  constructor(url: string) {
    super(`Document '${url}' export is taking too long to generate`)
    this.name = 'ExportPendingError'
  }
}

const isExportRetryable = (error: unknown) =>
  error instanceof ExportPendingError

type ExportCompleteFn = (message?: string) => void

export async function attemptDocumentExport(
  url: string,
  onExportComplete: ExportCompleteFn
) {
  const opts = {
    delay: makeBackoffDelay({ fixed: 500, base: 500 }),
    shouldRetry: isExportRetryable,
  }

  try {
    if (!url) {
      throw Error(`No URL provided`)
    }

    await retryWithDelay(async () => {
      const response = await apiRequest({
        method: 'GET',
        url: url,
        payload: undefined,
        requestDescription: `exporting ${url}`,
        responseType: 'blob',
      })

      if (response.status === 404) {
        throw new ExportPendingError(url)
      }

      if (!response.ok) {
        throw Error(
          `Document '${url}' export failed with status ${response.status}`
        )
      }

      onExportComplete()
    }, opts)
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    Logger.warn(`Document export failed: ${message}`)
    onExportComplete(message)
  }
}
